import React from 'react';
import { InputAdornment } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import useInputChange from '@replacements/hooks/useInputChange';
import Playback, {
  PlaybackProps,
} from '@replacements/components/PhoneticLibrary/Playback';

import { MAX_PHONETIC_REPLACEMENT_LENGTH } from '@replacements/hooks/usePhonetic';
import { TextFieldProps } from '../../../Inputs/TextField';
import { TextField } from '../../../Inputs';

export interface PhoneticInputProps
  extends Pick<TextFieldProps, 'endAdornment'> {
  id: string;
  name: string;
  placeholder: string;
  preview: ReturnType<typeof useInputChange>;
  loading: boolean;
  playbackProps: PlaybackProps;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange?: (value: string) => void;
}

const useStyles = makeStyles()(theme => ({
  inputAdorment: {
    position: 'absolute',
    right: 0,
  },
  textField: {
    borderRadius: theme.spacing(0.5),
    /**
     * NOTE: this will cause a bug for iphones where the input zooms in
     * this is due to an accessibility feature of iphones in particular
     * the request was to have the fontSize changed anyway
     */
    fontSize: theme.typography.body2.fontSize,
    // TODO: this height override should ultimately be part of text field
    minHeight: theme.spacing(2.75),
    // prevents the text field input from overlapping with the playback button
    '& input': {
      paddingRight: theme.spacing(4),
    },
  },
  playback: {
    '&:hover, &:focus': {
      backgroundColor: 'unset',
    },
  },
}));

const PhoneticInput = React.forwardRef<HTMLInputElement, PhoneticInputProps>(
  (
    {
      id,
      name,
      placeholder,
      preview,
      loading,
      playbackProps,
      disabled,
      autoFocus,
      onChange,
    },
    ref
  ) => {
    const { classes } = useStyles();

    const handleChange: React.ChangeEventHandler<
      HTMLInputElement | HTMLTextAreaElement
    > = e => {
      preview.onTextChange(e.target.value);
      if (onChange) onChange(e.target.value);
    };

    return (
      <TextField
        inputRef={ref}
        id={id}
        className={classes.textField}
        name={name}
        placeholder={placeholder}
        value={preview.text}
        onChange={handleChange}
        disabled={loading || disabled}
        autoFocus={autoFocus}
        type="text"
        autoComplete="off"
        endAdornment={
          <InputAdornment position="end" className={classes.inputAdorment}>
            <Playback
              {...playbackProps}
              className={classes.playback}
              disabledTooltip={
                preview.text.length > MAX_PHONETIC_REPLACEMENT_LENGTH
                  ? `Text exceeds the ${MAX_PHONETIC_REPLACEMENT_LENGTH} character limit`
                  : undefined
              }
            />
          </InputAdornment>
        }
      />
    );
  }
);

PhoneticInput.displayName = 'PhoneticInput';

export default PhoneticInput;
